/* eslint-disable @typescript-eslint/no-unused-vars */
import { CacheManager, CacheType, ICache } from '@descomplica/caching-browser';
import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import axios from 'axios';
import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ReactElement } from 'react';

import { GetCertificateEnabledCollaboration } from '@/application/collaborations/classroom/get-certificate-enabled.collaboration';
import { GetClassroomByCourseCollaboration } from '@/application/collaborations/classroom/get-classroom-by-course.collaboration';
import { GetCoursesForClassroomCollaboration } from '@/application/collaborations/classroom/get-courses-for-classroom.collaboration';
import { GetCoursesForClassroomByClassifierCollaboration } from '@/application/collaborations/classroom/get-courses-for-classroom-by-classifier.collaboration';
import { GetCurrentLessonByVideoGuidCollaboration } from '@/application/collaborations/classroom/get-current-lesson-by-video-guid.collaboration';
import {
  CurrentLessonNotFoundIdentifier,
  CurrentLessonUnexpectedErrorIdentifier,
  ObtainedCurrentLessonIdentifier,
} from '@/application/collaborations/classroom/get-current-lesson-by-video-guid.collaboration.responses';
import { GetCurrentVideoOnLessonsCollaboration } from '@/application/collaborations/classroom/get-current-video-on-lessons.collaboration';
import {
  CurrentVideoNotFoundIdentifier,
  CurrentVideoUnexpectedErrorIdentifier,
  ObtainedCurrentVideoIdentifier,
} from '@/application/collaborations/classroom/get-current-video-on-lessons.collaboration.responses';
import { GetStudyPlanCollaboration } from '@/application/collaborations/classroom/get-study-plan.collaboration';
import { RecordCourseProgressCollaboration } from '@/application/collaborations/classroom/record-course-progress-collaboration';
import { RecordLessonProgressCollaboration } from '@/application/collaborations/classroom/record-lesson-progress-collaboration';
import {
  LessonProgressRecordedAndVideoProgressRecordedBecauseWasCompleteIdentifier,
  LessonProgressRecordedBecauseWasCompleteIdentifier,
  LessonVideoProgressRecordedBecauseWasCompleteIdentifier,
} from '@/application/collaborations/classroom/record-lesson-progress-collaboration.responses';
import {
  RecordVideoWatchingCollaboration,
  WatchingLocation,
} from '@/application/collaborations/classroom/record-video-watching.collaboration';
import { GetAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-answer-by-guid.collaboration';
import { GetExercisesAnswerByGuidCollaboration } from '@/application/collaborations/exercises/get-exercises-answer-by-guid.collaboration';
import { GetExercisesByGuidCollaboration } from '@/application/collaborations/exercises/get-exercises-by-guid.collaboration';
import { GetCurrentStudentClassesCollaboration } from '@/application/collaborations/get-current-student-class.collaboration';
import { GetIdentifiedUserCollaboration } from '@/application/collaborations/get-identified-user.collaboration';
import { LESSON_TYPE, LiveStatus, TRANSMISSION_TYPE } from '@/application/commons/enums/live.enum';
import type {
  StreamingProviderType,
  UserDto,
} from '@/application/interfaces/classroom/pegasus.gateway';
import {
  MixpanelLessonConsumed,
  MixpanelLessonPlayed,
  MixpanelVideoConsumed,
} from '@/application/interfaces/classroom/tracking';
import type { AnswerDto } from '@/application/interfaces/exercises/pegasus.gateway';
import { InfrastructureError } from '@/application/interfaces/infrastructure-error';
import { LessonProgress } from '@/application/utils/lesson-progress';
import { Tracking } from '@/application/utils/tracking';
import { ClassifiersStore } from '@/presentation/stores/classifiers.store';
import { StudentClassesStore } from '@/presentation/stores/studentClasses.store';

export enum LessonType {
  LIVE = 'LIVE',
  PRE_RECORDED = 'PRE_RECORDED',
}

export type Video = {
  guid: string;
  lessonGuid: string;
  title: string;
  kalturaId: string;
  nextVideoGuid?: string;
  previousVideoGuid?: string;
  isCompleted: boolean;
  thumbUrl: string;
  progress?: number;
  duration?: number;
};

export type LessonExercisesDto = {
  lesson_guid: string;
  exercises_guid: string;
};

export type Exercises = {
  guid: string;
  lessonGuid: string;
  introduction?: string;
};

export type SupportMaterial = {
  url: string;
  guid: string;
};

export type Streaming = {
  guid: string;
  lessonGuid: string;
  title: string;
  streamingProvider: StreamingProviderType;
  streamingAccountId: string;
  streamingId: string;
  startStreaming: Date;
  endStreaming: Date;
  order: number;
  lessonStatus: string;
  description?: string;
};

export type Lesson = {
  guid: string;
  slug: string;
  name: string;
  teacher: string;
  thubmnail?: string;
  nextLessonGuid?: string;
  previousLessonGuid?: string;
  videos?: Array<Video>;
  streaming?: Streaming;
  isCompleted: boolean;
  exercises?: Exercises[];
  supportMaterial?: SupportMaterial;
  progress?: number;
  duration?: number;
  lessonType: string;
  order: number;
  thumbnail?: string;
  exercisesLesson?: LessonExercisesDto[];
};

export type Course = {
  guid: string;
  slug: string;
  name: string;
  description: string;
  lessons?: Lesson[];
};

export interface Classroom {
  course: Course;
  currentVideoGuid: string;
  studentRegistrationCode: string;
}

export type StudyPlanLessons = {
  guid: string;
  isCompleted: boolean;
};

export type ClassificationLevelDto = {
  guid: string;
  name: string;
};

export type ClassifierDto = {
  guid: string;
  name: string;
  classificationLevel: ClassificationLevelDto;
  childClassifiers: ClassifierDto[];
  courses: CourseDto[];
};

export type CourseDto = {
  guid: string;
  name: string;
  slug: string;
  description?: string;
  lessons?: Lesson[];
  isEnabled: boolean;
  canBeEnabled?: boolean;
  totalLessons?: number;
  totalHours?: number;
  thumbnail: string;
  classifiers: ClassifierDto[];
  isDeleted?: boolean;
  creationMoment: Date;
  isCompleted?: boolean;
  percentualProgress?: number;
};

export type CoursesForClassroomDto = {
  guid: string;
};

export type StudyPlanDto = {
  guid: string;
  classificationLevel: ClassificationLevelDto;
  weeks: WeeksDto[];
  startDate: Date;
  totalWeeks: number;
  totalLessons: number;
};

export type WeeksDto = {
  order: number;
  planned: CourseDto[];
};

export type ExerciseDTO = {
  guid: string;
  url: string;
};

export type SupportMaterialDto = {
  guid: string;
  url: string;
};

export type VideoDto = {
  guid: string;
  title: string;
  description?: string;
  duration: number;
  kalturaId: string;
  order: number;
};

export type StreamingDto = {
  guid: string;
  lessonGuid: string;
  title: string;
  description?: string;
  order: number;
  streamingAccountId: string;
  streamingId: string;
  startStreaming: Date;
  endStreaming: Date;
};

@ViewModel({ singleton: true })
export class ClassroomViewModel implements IReactiveModel {
  @observable classroom?: Classroom;
  @observable currentLesson?: Lesson;
  @observable currentVideo?: Video;
  @observable currentLiveLesson: Lesson;
  @observable currentLessonExercise: any;

  videoConsuptionTrackingArray: number[] = [];

  @observable mobileSidebarOpen = false;
  @observable isLoading = true;
  @observable errorMessage = '';
  @observable isRegisteringAdDisplay = false;
  @observable isVideoBannerVisible = false;
  @observable coursesForClassroom: string[] = [];
  @observable nextCoursesForClassroom = '';
  @observable nextVideoForLesson: Video = undefined;
  @observable autoPlayLessonEnabled = false;
  @observable startTime = 0;
  @observable hasHighestClassifierGuid = false;
  @observable studyPlan: StudyPlanDto;
  @observable student: UserDto;
  @observable studyPlanLessons: StudyPlanLessons[];
  @observable currentStudyPlanWeek: Lesson[] = [];
  @observable showCompletedStudyPlanOverlay = false;
  @observable hasAlreadyShownCompletedStudyPlanOverlay = false;
  @observable nextCourseGuid: string;
  @observable hasExerciseOpen = false;
  @observable exerciseUrl: string;
  @observable comesFromStudyPlan: boolean;
  @observable isCertificateEnabled: boolean;
  @observable hasCertificateOpen: boolean;
  @observable certificateUrl: string;
  @observable certificateMinimalPercentage: number;
  //related of exercises
  @observable showResult = false;
  @observable currentExercise?: LessonExercisesDto;
  @observable exercises;
  @observable isLoadingExercise = true;
  @observable currentQuestion;
  @observable selectedOption = null;
  @observable currentStep = 0;
  @observable answeredExercises = [];
  @observable exerciseLessonGuid: string;
  @observable exercisesGuid: string;
  @observable answeringQuestion: boolean;

  @observable tags: string[];
  @observable studentClassesGuid: string;
  @observable studentClassesTitle: string;

  videosPlayed: Video[] = [];
  registerProgressArray = [];
  registerVideoProgressArray = [];
  initialLessonProgress = 0;
  LESSON_PROGRESS_MARKS = [0, 0.1, 0.25, 0.5, 0.75, 1.0];
  initialVideoProgress = 0;
  VIDEO_PROGRESS_MARKS = [0, 0.1, 0.25, 0.5, 0.75, 1.0];

  libraryScreenCache: ICache = CacheManager.getCache(CacheType.LOCALSTORAGE, 'library-screen');

  constructor(
    private getClassroomByCourseCollaboration: GetClassroomByCourseCollaboration,
    private recordLessonProgressCollaboration: RecordLessonProgressCollaboration,
    private recordVideoWatchingCollaboration: RecordVideoWatchingCollaboration,
    private getCurrentLessonByVideoGuidCollaboration: GetCurrentLessonByVideoGuidCollaboration,
    private getCurrentVideoOnLessonsCollaboration: GetCurrentVideoOnLessonsCollaboration,
    private readonly coursesForClassroomCollaboration: GetCoursesForClassroomCollaboration,
    private readonly getCertificateEnabledCollaboration: GetCertificateEnabledCollaboration,
    private readonly getCoursesForClassroomByClassifierCollaboration: GetCoursesForClassroomByClassifierCollaboration,
    private recordCourseProgressCollaboration: RecordCourseProgressCollaboration,
    private getStudyPlanCollaboration: GetStudyPlanCollaboration,
    private getIdentifiedUser: GetIdentifiedUserCollaboration,
    private getExercisesByGuidCollaboration: GetExercisesByGuidCollaboration,
    private getExercisesAnswerByGuidCollaboration: GetExercisesAnswerByGuidCollaboration,
    private getAnswerByGuidCollaboration: GetAnswerByGuidCollaboration,
    private getCurrentStudentClassesCollaboration: GetCurrentStudentClassesCollaboration,
    private studentClassesStore: StudentClassesStore,
    private classifiersStore: ClassifiersStore,
  ) {
    makeObservable(this);

    reaction(
      () => this.currentExercise,
      async (currentExercise) => {
        if (currentExercise) {
          await this.handleGetExercises();
        }
      },
    );
  }

  @action
  async onInit() {
    this.isLoading = true;
  }

  @action
  async handleScreenEnter(
    guid: string,
    isStudyPlan?: boolean,
    weekOrder?: string,
    isLive?: boolean,
    streamingGuid?: string,
  ): Promise<void> {
    Tracking.pageView(this.studentClassesStore.getStudentClassesTrackingInfo());

    try {
      const classifierGuidFromCache = await this.getHighestClassifierSelected();

      this.classifiersStore.setClassifierGuidFromCache(classifierGuidFromCache);
      this.classifiersStore.findCourseClassifiers(guid);

      console.log('ClassroomViewModel.handleScreenEnter GetCoursesForClassroom');

      await this.GetCoursesForClassroom();

      const { data } = await this.getIdentifiedUser.run();

      if (data) {
        this.student = data;
      }

      console.log('ClassroomViewModel.handleScreenEnter', classifierGuidFromCache);
      if (classifierGuidFromCache) {
        const { data } = await this.getCurrentStudentClassesCollaboration.run(
          classifierGuidFromCache,
        );

        if (data) {
          this.tags = data.tags;
          this.studentClassesGuid = data.guid;
          this.studentClassesTitle = data.title;
        }

        await this.GetCoursesForClassroomByClassifier(classifierGuidFromCache);
        if (isStudyPlan) {
          this.comesFromStudyPlan = true;
          const { data } = await this.getStudyPlanCollaboration.run(classifierGuidFromCache);

          if (data) this.setStudyPlan(data);
        }
      } else {
        await this.GetCoursesForClassroom();
      }

      console.log('ClassroomViewModel.handleScreenEnter guid', guid);

      if (guid) await this.getNextCoursesForClassroom(guid);

      await this.setClassroomByCourseGuid(guid);

      await this.getCertificateEnabled();

      const lessons = this.studyPlan
        ? this.handleStudyPlan(
            guid,
            this.studyPlan,
            this.classroom?.course?.lessons || [],
            weekOrder,
          )
        : this.classroom?.course?.lessons || [];

      console.log('ClassroomViewModel.handleScreenEnter lessons', lessons);

      const currentLessonResponse = await this.getCurrentLessonByVideoGuidCollaboration.run({
        lessons,
        currentVideoGuid: this.classroom?.currentVideoGuid,
      });

      console.log(
        'ClassroomViewModel.handleScreenEnter currentLessonResponse',
        currentLessonResponse,
      );

      switch (currentLessonResponse.identifier) {
        case ObtainedCurrentLessonIdentifier:
        case CurrentLessonNotFoundIdentifier:
        case CurrentLessonUnexpectedErrorIdentifier:
          this.currentLesson = currentLessonResponse.data;
          break;
      }

      const currentVideoResponse = await this.getCurrentVideoOnLessonsCollaboration.run({
        lesson: this.currentLesson,
        currentVideoGuid: this.classroom?.currentVideoGuid,
      });
      switch (currentVideoResponse.identifier) {
        case ObtainedCurrentVideoIdentifier:
        case CurrentVideoNotFoundIdentifier:
        case CurrentVideoUnexpectedErrorIdentifier:
          if (this.currentLesson.lessonType === TRANSMISSION_TYPE.LIVE && isLive && streamingGuid) {
            this.currentVideo = currentVideoResponse.data;
          } else {
            this.currentVideo = currentVideoResponse.data;
            this.setStartTime(currentVideoResponse.data);
            this.handleLessonPlayed();
          }

          break;
      }

      if (this.studyPlan) {
        this.setStudyPlanLessonsArray(lessons);
      }

      this.resetProgressArray();
      this.resetVideoProgressArray();
      this.initialLessonProgress = this.currentLesson.progress;
      this.initialVideoProgress = this.currentVideo.progress;
      this.recordCourseProgress();

      if (isLive && streamingGuid) {
        const currentLiveLesson = lessons.find(
          (lesson) => lesson.streaming?.guid === streamingGuid,
        );
        this.setCurrentLesson(currentLiveLesson);
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async setClassroomByCourseGuid(guid: string): Promise<void> {
    this.isLoading = true;

    try {
      const response = await this.getClassroomByCourseCollaboration.run({
        guid,
      });

      const lessons = response.data.course.lessons;
      this.setCurrentLiveLesson(lessons);

      this.classroom = response.data;
      this.isLoading = false;
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async recordLessonProgress(
    guidCourse: string,
    guidVideo: string,
    usage: number,
    student: string,
  ): Promise<void> {
    try {
      const lessonProgress = LessonProgress.getLessonProgress(this.currentLesson, guidVideo, usage);

      const initialProgress = Math.round(this.initialLessonProgress ?? 0);
      const lastProgress = Math.round(lessonProgress * 100);

      if (lessonProgress > this.registerProgressArray[0]) {
        this.handleLessonConsumed(initialProgress, lastProgress);

        this.registerProgressArray.length > 0 && this.registerProgressArray.shift();
      }

      const { identifier } = await this.recordLessonProgressCollaboration.run({
        guidCourse,
        guidVideo,
        usage,
        student,
      });
      if (
        identifier === LessonProgressRecordedBecauseWasCompleteIdentifier ||
        identifier === LessonVideoProgressRecordedBecauseWasCompleteIdentifier ||
        identifier === LessonProgressRecordedAndVideoProgressRecordedBecauseWasCompleteIdentifier
      ) {
        if (this.currentVideo) {
          this.currentVideo.isCompleted = true;
        }
      }

      this.recordCourseProgress();
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async recordVideoProgress(guidVideo: string, usage: number): Promise<void> {
    try {
      const videoProgress = usage;

      const initialProgress = Math.round(this.initialVideoProgress ?? 0);

      if (Math.round(videoProgress * 10) / 10 >= this.registerVideoProgressArray[0]) {
        const lastProgress = this.registerVideoProgressArray[0] * 100;

        this.handleVideoConsumed(initialProgress, lastProgress);

        this.registerVideoProgressArray.length > 0 && this.registerVideoProgressArray.shift();
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async recordCourseProgress(): Promise<void> {
    try {
      await this.recordCourseProgressCollaboration.run({
        courseGuid: this.classroom.course.guid,
        studentGuid: this.student.guid,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  @action
  async recordVideoWatching(
    topicGuid: string,
    lessonGuid: string,
    videoGuid: string,
  ): Promise<void> {
    await this.recordVideoWatchingCollaboration.run({
      topicGuid,
      lessonGuid,
      videoGuid,
      watchingLocation: WatchingLocation.Library,
    });
  }

  @action
  toggleCompletedStudyplanOverlay = (): void => {
    this.showCompletedStudyPlanOverlay = !this.showCompletedStudyPlanOverlay;
  };

  @action
  toggleMobileSidebar = (): void => {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  };

  @action
  closeMobileSidebar = (): void => {
    this.mobileSidebarOpen = false;
  };

  @action
  setCurrentLiveLesson(lessons: Lesson[]): void {
    const liveLesson = lessons.find(
      (lesson) => lesson?.streaming?.lessonStatus === LiveStatus.LIVE,
    );

    this.currentLiveLesson = liveLesson;
  }

  @action
  async setShowNextContent(): Promise<void> {
    // eslint-disable-next-line no-debugger
    debugger;
    const classifierGuidFromCache = await this.getHighestClassifierSelected();
    if (classifierGuidFromCache) {
      this.hasHighestClassifierGuid = true;
    }
  }

  @action
  setCurrentLesson(currentLesson: Lesson): void {
    this.currentLesson = currentLesson;

    this.handleCompletedStudyPlanOverlay(currentLesson);
  }

  @action
  setCurrentVideo(video: Video): void {
    this.currentVideo = video;
    this.resetVideoProgressArray();
    this.setStartTime(video);
  }

  @action
  setNextVideoLesson(video: Video): void {
    this.nextVideoForLesson = video;
  }

  @action
  setAutoPlayLesson(state: boolean): void {
    this.autoPlayLessonEnabled = state;
  }

  @action
  setShowResult(result: boolean) {
    runInAction(() => {
      this.showResult = result;
    });
  }

  @action
  getAutoPlayLesson(): boolean {
    const stored = localStorage.getItem('course-preferences');
    this.autoPlayLessonEnabled = stored ? JSON.parse(stored).autoPlay : false;
    return this.autoPlayLessonEnabled;
  }

  @action
  async handleVideoPlaying(): Promise<void> {
    const lesson = this.classroom?.course.lessons?.find(
      (lesson) => lesson.guid === this.currentVideo?.lessonGuid,
    );
    const isVideoPlayed = this.videosPlayed.some((video) => video.guid === this.currentVideo?.guid);
    if (!isVideoPlayed && this.classroom && this.currentVideo && lesson) {
      this.videosPlayed.push(this.currentVideo);

      await this.getNextVideoForLesson();

      try {
        await this.recordVideoWatching(
          this.classroom.course.guid,
          lesson.guid,
          this.currentVideo.guid,
        );
      } catch (error) {
        this.handleError(error);
      }
    }
  }

  @action
  async GetCoursesForClassroom() {
    try {
      const { data } = await this.coursesForClassroomCollaboration.run();
      console.log('ClassroomViewModel.GetCoursesForClassroom data', data);

      const guidArray = data.map((course) => course.guid);
      this.coursesForClassroom = guidArray;
    } catch (error) {
      this.errorMessage = 'Ocorreu um erro ao tentar obter a biblioteca. Tente novamente.';
      this.handleError(error);
    }
  }

  @action
  async GetCoursesForClassroomByClassifier(classifierGuid: string) {
    try {
      const { data } = await this.getCoursesForClassroomByClassifierCollaboration.run(
        classifierGuid,
      );

      const guidArray = data.map((course) => course.guid);
      this.coursesForClassroom = guidArray;
    } catch (error) {
      this.errorMessage = 'Ocorreu um erro ao tentar obter a biblioteca. Tente novamente.';
      this.handleError(error);
    }
  }

  @action
  async getNextCoursesForClassroom(guid: string) {
    const currentCourseIndex = this.coursesForClassroom.indexOf(guid);

    this.nextCoursesForClassroom =
      currentCourseIndex === this.coursesForClassroom.length - 1
        ? ''
        : this.coursesForClassroom[currentCourseIndex + 1];
  }

  @action
  async getNextVideoForLesson() {
    const lesson = this.currentLesson;

    const nextVideo =
      lesson?.videos?.findIndex((videoSearch) => videoSearch?.guid === this.currentVideo.guid) + 1;

    this.setNextVideoLesson(lesson.videos[nextVideo]);
  }

  @action
  async getCertificateEnabled() {
    try {
      const { data } = await this.getCertificateEnabledCollaboration.run({
        courseGuid: this.classroom.course.guid,
      });

      this.isCertificateEnabled = data.enabled;
      this.certificateMinimalPercentage = data.courseMinimalPercentageNeeded;
    } catch (error) {
      this.errorMessage = 'Ocorreu um erro ao tentar obter o certificado. Tente novamente.';
      this.handleError(error);
    }
  }

  getCompletedVideoCountForLesson(lesson: Lesson): number {
    return lesson.videos?.filter((video: Video) => video.isCompleted).length || 0;
  }

  getVideoCountForLesson(lesson: Lesson): number {
    return lesson.videos?.length || 0;
  }

  getErrorMessage(): ReactElement | string {
    return this.errorMessage;
  }

  setStartTime(video: Video): void {
    if (video.duration && video.progress) {
      if (!video.isCompleted || (video.isCompleted && video.progress < 0.9)) {
        this.startTime = video.duration * video.progress;
      } else {
        this.startTime = 0;
      }
    } else {
      this.startTime = 0;
    }
  }

  resetProgressArray(): void {
    this.registerProgressArray.length = 0;
    this.registerProgressArray.push(...this.LESSON_PROGRESS_MARKS);
  }

  resetVideoProgressArray(): void {
    this.registerVideoProgressArray.length = 0;

    if (this.currentVideo.progress && !this.currentVideo.isCompleted) {
      const initialVideoProgressMarks = this.VIDEO_PROGRESS_MARKS.filter(
        (item) => item >= Math.round(this.currentVideo.progress * 10) / 10,
      );

      this.registerVideoProgressArray.push(...initialVideoProgressMarks);
    } else {
      this.registerVideoProgressArray.push(...this.VIDEO_PROGRESS_MARKS);
    }
  }

  @action
  async updateCurrentLesson(guid: string) {
    const response = await this.getClassroomByCourseCollaboration.run({
      guid,
    });

    const lessons = response.data.course.lessons;

    const currentLessonResponse = await this.getCurrentLessonByVideoGuidCollaboration.run({
      lessons,
      currentVideoGuid: this.currentVideo?.guid,
    });

    switch (currentLessonResponse.identifier) {
      case ObtainedCurrentLessonIdentifier:
      case CurrentLessonNotFoundIdentifier:
      case CurrentLessonUnexpectedErrorIdentifier:
        this.currentLesson = currentLessonResponse.data;
        break;
    }
  }

  @action
  async getHighestClassifierSelected() {
    const cache = await this.libraryScreenCache.get('last-selected-option');

    if (typeof cache === 'string' && cache) {
      this.hasHighestClassifierGuid = true;
      return cache;
    }
  }

  @action
  setStudyPlan(studyPlan: StudyPlanDto): void {
    this.studyPlan = studyPlan;
  }

  handleStudyPlan(
    courseGuid: string,
    studyPlan: StudyPlanDto,
    lessons: Lesson[],
    weekOrder: string,
  ): Lesson[] {
    const selectedWeek = studyPlan.weeks.find((week) => week.order === Number(weekOrder));

    const selectedCourse = selectedWeek.planned.find((course) => course.guid === courseGuid);

    const currentIndex = selectedWeek.planned.indexOf(selectedCourse);

    this.nextCourseGuid = selectedWeek.planned[currentIndex + 1]
      ? selectedWeek.planned[currentIndex + 1].guid
      : '';

    const selectedLessons = selectedCourse.lessons;

    const selectedLessonsInfo = lessons.filter((lesson) =>
      selectedLessons.some((selectedLesson) => lesson.guid === selectedLesson.guid),
    );

    return selectedLessonsInfo;
  }

  handleLiveLessonPlayed() {
    const props: MixpanelLessonPlayed = {
      lesson_type: LESSON_TYPE.LIVE,
      type: TRANSMISSION_TYPE.LIVE,
      lesson: this.currentLesson.slug,
      lesson_name: this.currentLesson.name,
      lesson_id: this.currentLesson.guid,
      course: this.classroom.course.slug,
      course_name: this.classroom.course.name,
      course_id: this.classroom.course.guid,
      teacher: this.currentLesson.teacher,
      rewatch: this.currentLesson.isCompleted,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.lessonPlayed(props);
  }

  handleLessonPlayed() {
    const lessonPlayedProps: MixpanelLessonPlayed = {
      lesson_type: LESSON_TYPE.VOD,
      type: TRANSMISSION_TYPE.RECORDED_LESSON,
      lesson: this.currentLesson.slug,
      lesson_name: this.currentLesson.name,
      lesson_id: this.currentLesson.guid,
      course: this.classroom.course.slug,
      course_name: this.classroom.course.name,
      course_id: this.classroom.course.guid,
      teacher: this.currentLesson.teacher,
      rewatch: this.currentLesson.isCompleted,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.lessonPlayed(lessonPlayedProps);
  }

  handleLessonConsumed(initialProgress: number, lastProgress: number) {
    const lessonConsumedProps: MixpanelLessonConsumed = {
      lesson_type: LESSON_TYPE.VOD,
      type: TRANSMISSION_TYPE.RECORDED_LESSON,
      lesson: this.currentLesson.slug,
      lesson_name: this.currentLesson.name,
      lesson_id: this.currentLesson.guid,
      course: this.classroom.course.slug,
      course_name: this.classroom.course.name,
      course_id: this.classroom.course.guid,
      teacher: this.currentLesson.teacher,
      rewatch: this.currentLesson.isCompleted,
      initial_progress: initialProgress,
      last_progress: lastProgress,
      lesson_duration: this.currentLesson.duration,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.lessonConsumed(lessonConsumedProps);
  }

  handleVideoConsumed(initialProgress: number, lastProgress: number) {
    const videoConsumedProps: MixpanelVideoConsumed = {
      lesson_type: LESSON_TYPE.VOD,
      type: TRANSMISSION_TYPE.RECORDED_LESSON,
      lesson: this.currentLesson.slug,
      lesson_name: this.currentLesson.name,
      lesson_id: this.currentLesson.guid,
      course: this.classroom.course.slug,
      course_name: this.classroom.course.name,
      course_id: this.classroom.course.guid,
      teacher: this.currentLesson.teacher,
      rewatch: this.currentVideo.isCompleted,
      initial_progress: initialProgress,
      last_progress: lastProgress,
      video_duration: this.currentVideo.duration,
      video_guid: this.currentVideo.guid,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.videoConsumed(videoConsumedProps);
  }

  handleAutoplayChanged(autoplayLessonEnabled) {
    const autoplayChanged = {
      course: this.classroom?.course.slug,
      course_id: this.classroom?.course.guid,
      course_name: this.classroom?.course.name,
      lesson: this.currentLesson.slug,
      lesson_id: this.currentLesson.guid,
      lesson_name: this.currentLesson.name,
      teacher: this.currentLesson.teacher,
      state_autoplay: !autoplayLessonEnabled,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.autoplayChanged(autoplayChanged);
  }

  handleExerciseViewed(lesson, url) {
    const exerciseViewed = {
      course: this.classroom.course.slug,
      course_id: this.classroom.course.guid,
      course_name: this.classroom.course.name,
      lesson: lesson.slug,
      lesson_id: lesson.guid,
      lesson_name: lesson.name,
      teacher: lesson.teacher,
      material_URL: url,
      ...this.classifiersStore.classifiersTrackingInfo,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
    };

    Tracking.exerciseViewed(exerciseViewed);
  }

  handleSupportMaterialOpened(lesson, supportMaterial) {
    const trackSupportMaterial = {
      course: this.classroom.course.slug,
      course_id: this.classroom.course.guid,
      course_name: this.classroom.course.name,
      lesson: lesson.slug,
      lesson_id: lesson.guid,
      lesson_name: lesson.name,
      teacher: lesson.teacher,
      material_URL: supportMaterial.url,
      ...this.studentClassesStore.getStudentClassesTrackingInfo(),
      ...this.classifiersStore.classifiersTrackingInfo,
    };

    Tracking.supportMaterialOpened(trackSupportMaterial);
  }

  setStudyPlanLessonsArray(lessons: Lesson[]) {
    this.currentStudyPlanWeek = lessons;

    const selectedLessonsGuid = lessons.map((lesson) => ({
      guid: lesson.guid,
      isCompleted: lesson.isCompleted,
    }));

    this.studyPlanLessons = selectedLessonsGuid;
  }

  handleCompletedStudyPlanOverlay(currentLesson: Lesson) {
    const selectedLessonsAreCompleted = this.studyPlanLessons?.every(
      (lesson) => lesson.isCompleted,
    );
    const isLessonNotOnStudyPlan = this.studyPlanLessons?.every(
      (lesson) => lesson.guid !== currentLesson.guid,
    );

    if (
      selectedLessonsAreCompleted &&
      isLessonNotOnStudyPlan &&
      !this.hasAlreadyShownCompletedStudyPlanOverlay
    ) {
      this.showCompletedStudyPlanOverlay = true;
      this.hasAlreadyShownCompletedStudyPlanOverlay = true;
    }
  }

  handleWatchAgain() {
    const currentStudyPlanWeek = this.currentStudyPlanWeek;
    this.currentLesson = currentStudyPlanWeek[0];
    this.currentVideo = currentStudyPlanWeek[0].videos[0];
  }

  handleContinueStudyPlan(weekOrder: string) {
    const getCourseUrl = () => {
      return this.nextCourseGuid
        ? `/classroom/study-plan/${weekOrder}/course/${this.nextCourseGuid}`
        : `/study-plan`;
    };

    window.location.href = getCourseUrl();
  }

  private handleError(error: unknown) {
    console.error('ClassroomViewModel.handleError');
    if (axios.isAxiosError(error)) {
      console.error('axios.isAxiosError', error);
      const data = error?.response?.data;
      if (!data?.identifier) {
        throw new InfrastructureError(data);
      }

      throw error;
    }

    console.error('Unknown Error:', error);
    throw new InfrastructureError(error);
  }

  /* START RELATED OF EXERCISES */
  @action.bound
  setHasExerciseOpen(open: boolean, url?: string, exercise?: LessonExercisesDto): void {
    this.exerciseUrl = '';

    if (url) {
      this.exerciseUrl = url;
    }

    if (exercise) {
      this.setCurrentExercise(exercise);
    }

    this.closeMobileSidebar();
    this.hasExerciseOpen = open;
    this.hasCertificateOpen = false;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async handleGetExercises() {
    this.answeredExercises = [];
    try {
      this.exerciseLessonGuid = this.currentExercise?.lesson_guid;
      this.exercisesGuid = this.currentExercise?.exercises_guid;

      const { data: exercises } = await this.getExercisesByGuidCollaboration.run(
        this.exercisesGuid,
      );

      runInAction(() => {
        this.exercises = exercises;
      });

      if (this.exercises && this.exercises.exercisesQuestion.length > 0) {
        const { data: answerExercises } = await this.getExercisesAnswerByGuidCollaboration.run({
          guid: this.exercisesGuid,
          lessonGuid: this.exerciseLessonGuid,
        });

        runInAction(() => {
          if (answerExercises && answerExercises.answers.length > 0) {
            const questions = this.exercises.exercisesQuestion.map((exercise) => {
              answerExercises.answers.map((answer) => {
                if (answer.question_guid === exercise.question_guid) {
                  exercise.answered = true;
                  exercise.correct = answer.correct;
                  exercise.choices_guid = answer.choices_guid;
                  exercise.correct_choice = answer.correct_choice;

                  this.answeredExercises.push({
                    choices_guid: exercise.choices_guid,
                    question_guid: exercise.question_guid,
                  });
                }
              });

              return exercise;
            });

            this.exercises = {
              ...this.exercises,
              exercisesQuestion: questions,
            };
          }
        });
      }

      if (this.answeredExercises?.length === this.exercises?.exercisesQuestion.length) {
        runInAction(() => {
          this.setShowResult(true);
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.isLoadingExercise = false;
      });
    }
  }

  @action.bound
  setCurrentExercise(currentExercise: LessonExercisesDto) {
    this.currentExercise = currentExercise;
  }

  @action
  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  @action.bound
  setCurrentQuestion(question: any) {
    this.currentQuestion = question;
  }

  @action.bound
  setSelectedOption(selectedOption: string | null) {
    this.selectedOption = selectedOption;
  }

  @action
  async getAnswerQuestion(dto: AnswerDto) {
    try {
      await this.getAnswerByGuidCollaboration.run(dto);
      await this.handleGetExercises().then(() => {
        this.answeringQuestion = false;
        if (
          this.currentStep + 1 === this.exercises.exercisesQuestion.length &&
          this.answeredExercises.length === this.exercises.exercisesQuestion.length
        ) {
          this.setShowResult(true);
        } else {
          this.setCurrentStep(this.currentStep + 1);
          this.setSelectedOption(null);
        }

        this.setCurrentQuestion(this.exercises.exercisesQuestion[this.currentStep]);
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  setIsLoading(loading: boolean) {
    this.isLoadingExercise = loading;
  }

  /* END OF RELATED EXERCISES*/

  @action.bound
  setHasCertificateOpen(open: boolean, url?: string): void {
    this.certificateUrl = '';

    if (url) {
      this.certificateUrl = url;
    }
    this.hasCertificateOpen = open;
    this.hasExerciseOpen = false;
  }
}
